import React, { useEffect, useMemo } from 'react';
import styles from './PaymentGateway.module.scss';

/* Redux */
import { connect } from 'react-redux';
import { LoginState } from '../../store/login/reducer';
import { RootState } from '../../store';

/* Translations */
import { useTranslation } from 'react-i18next';
import { tr } from '../../utils/translations/translator';

/* Types */
import { Shop } from '../Shops/shared/interfaces';
import { StoreType, StoreTypesReversed } from './interfaces/store_types';
import { Product } from './interfaces/Product';
import { PremiumShop } from '../Shops/Premium/PremiumShop';
import { PremiumProduct } from '../Shops/Premium/interfaces/PremiumProduct';
import { TipShop } from '../Shops/Tip/TipShop';
import { PaidTipProduct } from '../Shops/Tip/interfaces/TipProduct';

/* Components */
import { InitButton } from './stages/InitButton';
import { UserInfoConnected } from './stages/UserInfo';
import { SelectPayment } from './stages/SelectPayment';
import { PaymentSuccessPremium } from './stages/PaymentSuccessPremium';

/* Providers */
import {
  PurchaseStatus,
  usePurchaseStatusProvider,
} from '../Shops/shared/__providers__/PurchaseStatusProvider';
import { usePaymentStageContext } from './__providers__/PaymentStageContext';
import { useAnalyticsHandlers } from '../../analytics/__providers__/AnalyticsHandlersProvider';
import { usePaymentTypeContext } from './__providers__/PaymentTypeContext';

interface StateProps {
  loginState: LoginState;
}
interface PaymentGatewayProps {
  storeType: StoreType;
  shop: Shop;
  tipProduct?: Product;
}
type StageType =
  | 'INIT_BUTTON'
  | 'USER_INFO'
  | 'SELECT_PAYMENT'
  | 'PAYMENT_SUCCESS'
  | 'PAYMENT_FAILURE';

export const PaymentGateway: React.FC<PaymentGatewayProps & StateProps> = ({
  shop,
  loginState,
  storeType,
  tipProduct,
}) => {
  const { t } = useTranslation();
  const { purchaseStatus } = usePurchaseStatusProvider();
  const { selectedPayment } = usePaymentTypeContext();
  const analytics = useAnalyticsHandlers();

  const { currentStage, setCurrentStage } = usePaymentStageContext();
  useEffect(() => {
    if (storeType === StoreTypesReversed.Premium) {
      if (purchaseStatus === PurchaseStatus.COMPLETED) {
        setCurrentStage('PAYMENT_SUCCESS');

        const premiumShop = shop as PremiumShop;
        analytics?.client.Mixpanel.log('WEB_PREMIUM_PURCHASE_COMPLETED', [
          {
            tipster_id: premiumShop.tipsterInfo.id,
            tipster_name: premiumShop.tipsterInfo.name,
            premium_plan_type: (premiumShop.selectedProduct as PremiumProduct)
              .template.code,
            price_amount: (premiumShop.selectedProduct as PremiumProduct).price
              .buildPrice()
              .toNumber(),
            currency: (premiumShop.selectedProduct as PremiumProduct).price
              .currency,
            discount: (premiumShop.selectedProduct as PremiumProduct)
              .discountPercentage,
            payment_method: selectedPayment?.toString(),
          },
        ]);
      }
    }

    if (storeType === StoreTypesReversed.TipPurchase) {
      if (purchaseStatus === PurchaseStatus.COMPLETED) {
        const tipShop = shop as TipShop;
        analytics?.client.Mixpanel.log('WEB_TIP_PURCHASE_COMPLETED', [
          {
            tipster_id: tipShop.tipsterInfo.id,
            tipster_name: tipShop.tipsterInfo.name,
            tip_id: (tipShop.selectedProduct as PaidTipProduct).tip.id,
            price_amount: (tipShop.selectedProduct as PaidTipProduct).price
              .buildPrice()
              .toNumber(),
            currency: (tipShop.selectedProduct as PaidTipProduct).price
              .currency,
            discount: (tipShop.selectedProduct as PaidTipProduct)
              .discountPercentage,
            payment_method: selectedPayment?.toString(),
          },
        ]);
      }
    }
    if (purchaseStatus === PurchaseStatus.FAILED) {
      setCurrentStage('PAYMENT_FAILURE');

      switch (storeType) {
        case StoreTypesReversed.Premium:
          const premiumShop = shop as PremiumShop;
          analytics?.client.Mixpanel.log('WEB_PREMIUM_PURCHASE_FAILED', [
            {
              tipster_id: premiumShop.tipsterInfo.id,
              tipster_name: premiumShop.tipsterInfo.name,
              premium_plan_type: (premiumShop.selectedProduct as PremiumProduct)
                .template.code,
              price_amount: (
                premiumShop.selectedProduct as PremiumProduct
              ).price
                .buildPrice()
                .toNumber(),
              currency: (premiumShop.selectedProduct as PremiumProduct).price
                .currency,
              discount: (premiumShop.selectedProduct as PremiumProduct)
                .discountPercentage,
              payment_method: selectedPayment?.toString(),
            },
          ]);
          break;
        case StoreTypesReversed.TipPurchase:
          const tipShop = shop as TipShop;
          analytics?.client.Mixpanel.log('WEB_TIP_PURCHASE_FAILED', [
            {
              tipster_id: tipShop.tipsterInfo.id,
              tipster_name: tipShop.tipsterInfo.name,
              tip_id: (tipShop.selectedProduct as PaidTipProduct).tip.id,
              price_amount: (tipShop.selectedProduct as PaidTipProduct).price
                .buildPrice()
                .toNumber(),
              currency: (tipShop.selectedProduct as PaidTipProduct).price
                .currency,
              discount: (tipShop.selectedProduct as PaidTipProduct)
                .discountPercentage,
              payment_method: selectedPayment?.toString(),
            },
          ]);
          break;
        default:
          break;
      }
    }
  }, [purchaseStatus]);

  const nextStage: StageType = useMemo(() => {
    switch (currentStage) {
      case 'INIT_BUTTON':
        if (loginState.user?.user) {
          return 'SELECT_PAYMENT';
        }
        return 'USER_INFO';
      default:
        return 'INIT_BUTTON';
    }
  }, [currentStage]);
  const previousStage: StageType = useMemo(() => {
    switch (currentStage) {
      case 'USER_INFO':
        return 'INIT_BUTTON';
      case 'SELECT_PAYMENT':
        if (loginState.user?.user) {
          return 'INIT_BUTTON';
        }
        return 'USER_INFO';
      default:
        return 'INIT_BUTTON';
    }
  }, [currentStage]);

  const handleBackButton = (event: any) => {
    event.preventDefault();
    setCurrentStage(previousStage);
  };
  // useEffect(() => {
  //   window.addEventListener('popstate', handleBackButton);
  //   return () => {
  //     window.removeEventListener('popstate', handleBackButton);
  //   };
  // }, []);

  const paymentProduct = useMemo(() => {
    if (storeType === 'PREMIUM') {
      const premiumShop = shop as PremiumShop;
      return new Product({
        id: premiumShop.selectedProduct.plan.id,
        name: premiumShop.selectedProduct.template.code,
        price: premiumShop.selectedProduct.price,
        discount: premiumShop.selectedProduct.priceWithDiscount,
        discount_percentage: premiumShop.selectedProduct.discountPercentage,
      });
    } else {
      return tipProduct as Product;
    }
  }, [shop.selectedProduct]);

  useEffect(() => {
    if (loginState.user?.user && currentStage === 'USER_INFO') {
      setCurrentStage('SELECT_PAYMENT');
    }
    if (!loginState.user?.user && currentStage === 'SELECT_PAYMENT') {
      setCurrentStage('INIT_BUTTON');
    }
  }, [currentStage]);

  const currentComponent: JSX.Element = useMemo(() => {
    switch (currentStage) {
      case 'INIT_BUTTON':
        return (
          <InitButton
            action={() => setCurrentStage(nextStage)}
            store={storeType}
          ></InitButton>
        );
      case 'USER_INFO':
        return (
          <UserInfoConnected storeType={storeType}>
            <button
              className={styles.backButton}
              onClick={(e: any) => handleBackButton(e)}
            >
              {tr(t, 'back')}
            </button>
          </UserInfoConnected>
        );
      case 'SELECT_PAYMENT':
        return (
          <SelectPayment
            storeType={storeType}
            product={paymentProduct}
            shop={shop}
          >
            <button
              className={styles.backButton}
              onClick={(e: any) => handleBackButton(e)}
            >
              {tr(t, 'back')}
            </button>
          </SelectPayment>
        );
      case 'PAYMENT_SUCCESS':
        const product = shop.selectedProduct as PremiumProduct;
        return (
          <PaymentSuccessPremium
            tipsterId={shop.tipsterInfo.id}
            tipsterName={shop.tipsterInfo.name}
            until={product.afterPurchaseExpirationDate.format('DD Month')}
            referralCode={shop.tipsterInfo.referral_code}
          />
        );

      default:
        return (
          <InitButton
            action={() => setCurrentStage(nextStage)}
            store={storeType}
          ></InitButton>
        );
    }
  }, [currentStage]);

  return <div className={styles.container}>{currentComponent}</div>;
};

const mapStateToProps = (state: RootState) => ({
  loginState: state.login,
});

export const PaymentGatewayConnected = connect(mapStateToProps)(PaymentGateway);
